/**
 * Character XP → level curve (B5 progression spine).
 *
 * Real KTS HeroLevel veterancy (BehaviorData.xml:1357-1453): 320 XP to reach L2,
 * then +150/level. v1 caps at L10 — KTS grants expertise points only for the
 * first 10 levels (MS:47533), so leveling past 10 is meaningless for progression.
 *
 * Convention: players spawn at L1 with 0 XP. `xpForLevel(1) === 0`. Reaching L2
 * costs 320 cumulative, L3 470, … L10 1520. (KTS's veterancy array is offset by
 * one from this spawn-at-L1 convention — its "level 1 = 320" is our reach-L2.)
 *
 * Shared so the client level badge / XP ring and the server level-up grant agree.
 */

export const LEVEL_CAP = 10;
// v1 "compressed 5-tier" model: 1 point per level-up; trees have 5 tiers; each v1
// tier = 5 KTS skill-levels of stats + that band's KTS perk. (KTS grants 5 pts/level
// over 25-level trees; v1 collapses 5:1, so 1 v1-point = 5 KTS-points = 1 tier.)
export const POINTS_PER_LEVEL = 1;
export const STARTING_POINTS = 1; // spawn at L1 with 1 banked point (05-heroes-progression.md)

// Cumulative XP to REACH each level (index = level, 1..LEVEL_CAP). L1 = 0.
const THRESHOLDS: number[] = (() => {
  const t: number[] = [0, 0]; // index 0 unused; L1 = 0
  for (let lvl = 2; lvl <= LEVEL_CAP; lvl++) {
    t[lvl] = (t[lvl - 1] ?? 0) + (lvl === 2 ? 320 : 150);
  }
  return t;
})();

/** Cumulative XP required to reach level `n` (1..LEVEL_CAP). L1 = 0; clamps at cap. */
export function xpForLevel(n: number): number {
  if (n <= 1) return 0;
  const idx = n >= LEVEL_CAP ? LEVEL_CAP : n;
  return THRESHOLDS[idx] ?? 0;
}

/** Character level for a total XP amount. Starts at 1; clamps at LEVEL_CAP. */
export function levelForXp(xp: number): number {
  let lvl = 1;
  for (let n = 2; n <= LEVEL_CAP; n++) {
    if (xp >= (THRESHOLDS[n] ?? Infinity)) lvl = n;
    else break;
  }
  return lvl;
}
